import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { partnerAdminService } from '../services/api';
import { Lock, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';

const PartnerActivatePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invitationId = params.get('invitation_id');
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 10) { setErr('Password must be at least 10 characters.'); return; }
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await partnerAdminService.activate({ invitation_id: invitationId, token, password });
      setDone(true);
      setTimeout(() => navigate('/login?activated=1'), 1800);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Activation failed. The invitation may be expired or already used.');
    } finally { setSaving(false); }
  };

  if (!invitationId || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center bg-white border border-gray-200 rounded-xl p-8" data-testid="partner-activate-invalid">
          <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-900">Invalid activation link</h1>
          <p className="text-gray-600 mt-2 text-sm">This link is missing required information. Please use the invitation link BlueLock sent you.</p>
          <Link to="/login" className="inline-block mt-4 text-blue-600 hover:underline text-sm">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8" data-testid="partner-activate-page">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-6 h-6 text-blue-700" /><span className="text-lg font-bold text-blue-900">BlueLock Partner Portal</span></div>
        <h1 className="text-xl font-semibold text-gray-900 mt-2">Activate your account</h1>
        <p className="text-gray-600 text-sm mt-1">Choose a password to finish setting up your partner access.</p>
        {done ? (
          <div className="mt-6 text-center" data-testid="partner-activate-success">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <p className="text-green-700 font-medium">Account activated! Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">New password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="partner-activate-password" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Confirm password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="partner-activate-confirm" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            {err && <p role="alert" className="text-sm text-rose-600" data-testid="partner-activate-error">{err}</p>}
            <button type="submit" disabled={saving} data-testid="partner-activate-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Activate account
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PartnerActivatePage;
